/**
 * The page of a report group (UI3 A, 2026-10-23).
 *
 * A group is N reports of one project — the tabs of one portal page —
 * that share ONE filter set. This page edits what is the group's: the
 * order of its reports and the table filter × report: per filter and per report, the report USES it (at
 * which of its columns), it does NOT APPLY (an explicit decision, the
 * reason optional), or the decision is MISSING («falta»), which blocks
 * the publish and is counted on the button.
 *
 * The filter DEFINITIONS (type, labels, options, defaults) are shown, not
 * edited here: like every structural edit of a dashboard, they go through
 * the report-builder skill / API (``PUT /api/builder/report-groups/{key}``).
 *
 * A save is live for the group's PUBLISHED reports, so the server checks
 * each of them the way Publish does and refuses the save with the list.
 *
 * WHO is a tab is not edited here (2026-10-25): a project shows its
 * reports as tabs or as a list, never both, so every report of a project
 * in tabs is a tab. A new report of the project joins the group with its
 * decisions «falta»; to take reports out, the project switches to a list
 * (its Settings).
 */
import { Badge, Button } from "@omnitok/ui";
import { ArrowDown, ArrowLeft, ArrowUp, ExternalLink, Minus } from "lucide-react";
import { Callout, PageHeader, Section, StatusChip } from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { resolveLocalized, type DisplayLabel } from "@pervasivemind/dashboard-render";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useT } from "@/i18n";
import { HttpError } from "@/lib/api";
import { draftsApi, viewsConflictOf } from "@/lib/builder-api";
import { reportGroupsApi } from "@/lib/report-groups-api";
import { BrokenViewsDialog } from "@/pages/builder/BrokenViewsWarning";
import type { AiHomeViewsConflict, ReportDraft, ReportGroup, ReportGroupDecision } from "@/types";

type Bindings = ReportGroup["config"]["bindings"];

export function errorLines(err: unknown): string[] {
  if (err instanceof HttpError) {
    const payload = err.detail as { detail?: unknown } | undefined;
    const detail = payload?.detail;
    if (detail && typeof detail === "object" && Array.isArray((detail as { errors?: unknown }).errors)) {
      const d = detail as { message?: string; errors: unknown[] };
      return [d.message ?? err.message, ...d.errors.map(String)];
    }
    if (typeof detail === "string") return [detail];
    return [err.message];
  }
  return [err instanceof Error ? err.message : String(err)];
}

/** «Falta» cells of the edited state (the server recounts on save). */
function countPending(group: ReportGroup, bindings: Bindings, members: string[]): number {
  let n = 0;
  for (const f of group.config.filters) {
    for (const m of members) if (!bindings[m]?.[f.key]) n += 1;
  }
  return n;
}

export function ReportGroupPage(): JSX.Element {
  const { t, locale } = useT();
  const { slug = "", groupKey = "" } = useParams<{ slug: string; groupKey: string }>();
  const [group, setGroup] = useState<ReportGroup | null>(null);
  const [drafts, setDrafts] = useState<ReportDraft[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [bindings, setBindings] = useState<Bindings>({});
  const [members, setMembers] = useState<string[]>([]);
  const [busy, setBusy] = useState<"save" | "publish" | null>(null);
  /** Saved AI Home views the publish would break (the server's 409). */
  const [viewsConflict, setViewsConflict] = useState<AiHomeViewsConflict | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  const adopt = useCallback((g: ReportGroup) => {
    setGroup(g);
    setBindings(structuredClone(g.config.bindings ?? {}));
    setMembers(g.members.map((m) => m.key));
  }, []);

  useEffect(() => {
    let alive = true;
    Promise.all([reportGroupsApi.get(groupKey), draftsApi.list()])
      .then(([g, list]) => {
        if (!alive) return;
        adopt(g);
        setDrafts(list.drafts);
      })
      .catch((err: unknown) => {
        if (alive) setLoadError(errorLines(err).join(" — "));
      });
    return () => {
      alive = false;
    };
  }, [groupKey, adopt]);

  const label = (value: unknown, fallback: string): string =>
    resolveLocalized(value as DisplayLabel, locale) || fallback;

  const draftByKey = useMemo(() => new Map(drafts.map((d) => [d.key, d])), [drafts]);

  if (loadError) {
    return (
      <div className="p-6">
        <Callout tone="danger" title={t("reportGroup.loadError")}>
          {loadError}
        </Callout>
      </div>
    );
  }
  if (!group) {
    return (
      <div className="grid h-64 place-items-center">
        <Spinner size={20} ariaLabel={t("common.loading")} />
      </div>
    );
  }

  const dirty =
    JSON.stringify(bindings) !== JSON.stringify(group.config.bindings ?? {}) ||
    JSON.stringify(members) !== JSON.stringify(group.members.map((m) => m.key));
  const pending = countPending(group, bindings, members);

  const setDecision = (filterKey: string, reportKey: string, decision: ReportGroupDecision | null) => {
    setBindings((prev) => {
      const next = structuredClone(prev);
      const row = { ...(next[reportKey] ?? {}) };
      if (decision === null) delete row[filterKey];
      else row[filterKey] = decision;
      next[reportKey] = row;
      return next;
    });
  };

  const move = (index: number, delta: number) => {
    setMembers((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  };

  const save = async (): Promise<ReportGroup | null> => {
    setBusy("save");
    setErrors([]);
    setNotice(null);
    try {
      const saved = await reportGroupsApi.update(group.key, {
        config: { ...group.config, filters: group.config.filters, bindings },
        members,
      });
      adopt(saved);
      setNotice(t("reportGroup.saved"));
      return saved;
    } catch (err) {
      setErrors(errorLines(err));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const publish = async (confirmBrokenViews = false) => {
    if (dirty && (await save()) === null) return;
    setBusy("publish");
    setErrors([]);
    setNotice(null);
    try {
      adopt(await reportGroupsApi.publish(group.key, confirmBrokenViews));
      setViewsConflict(null);
      setNotice(t("reportGroup.published"));
    } catch (err) {
      const conflict = viewsConflictOf(err);
      if (conflict !== null) setViewsConflict(conflict);
      else setErrors(errorLines(err));
    } finally {
      setBusy(null);
    }
  };

  const groupName = label(group.display_name, group.key);

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-6">
      {viewsConflict !== null && (
        <BrokenViewsDialog
          conflict={viewsConflict}
          title={t("views.publishGroupTitle")}
          body={t("views.publishGroupBody")}
          keepLabel={t("publish.close")}
          goLabel={t("publish.confirmAnyway")}
          busy={busy === "publish"}
          live={false}
          onKeep={() => setViewsConflict(null)}
          onGo={() => void publish(true)}
        />
      )}
      <PageHeader
        eyebrow={
          <Link
            to={`/projects/${slug}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-neutral-500 hover:text-neutral-800"
          >
            <ArrowLeft size={12} aria-hidden />
            {t("reportGroup.backToProject")}
          </Link>
        }
        title={groupName}
        subtitle={t("reportGroup.subtitle")}
        actions={
          <>
            <Button variant="outline" size="sm" disabled={!dirty || busy !== null} onClick={() => void save()}>
              {busy === "save" ? t("reportGroup.saving") : t("reportGroup.save")}
            </Button>
            <Button
              size="sm"
              disabled={pending > 0 || busy !== null}
              onClick={() => void publish()}
              title={pending > 0 ? t("reportGroup.pendingHint") : undefined}
            >
              {pending > 0
                ? t("reportGroup.publishPending", { count: pending })
                : busy === "publish"
                  ? t("reportGroup.publishing")
                  : t("reportGroup.publish")}
            </Button>
          </>
        }
      />

      {errors.length > 0 && (
        <div className="mb-4">
          <Callout tone="danger" title={errors[0]!}>
            <ul className="list-disc pl-5">
              {errors.slice(1).map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </Callout>
        </div>
      )}
      {notice && errors.length === 0 && (
        <p role="status" className="mb-4 text-xs font-medium text-emerald-700">
          {notice}
        </p>
      )}

      <div className="flex flex-wrap items-start gap-4">
        <div className="w-full min-w-0 lg:w-[300px] lg:flex-none">
          <Section title={t("reportGroup.reports")} description={t("reportGroup.reportsHint")}>
            <ol>
              {members.map((key, index) => {
                const d = draftByKey.get(key);
                return (
                  <li key={key} className="flex items-center gap-2 border-t border-neutral-200 py-2 text-sm first:border-t-0">
                    <span className="w-5 text-right text-xs text-neutral-500">{index + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-neutral-900">
                        {label(d?.report_config.display_name, key)}
                      </p>
                      <p className="truncate font-mono text-xs text-neutral-500">{key}</p>
                    </div>
                    {d &&
                      (d.status === "published" ? (
                        <StatusChip label={t("list.status.published")} tone="success" />
                      ) : (
                        <StatusChip label={t("list.status.draft")} tone="neutral" />
                      ))}
                    <Link
                      to={`/builder/${key}`}
                      aria-label={t("reportGroup.openReport", { key })}
                      className="rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
                    >
                      <ExternalLink size={14} aria-hidden />
                    </Link>
                    <button
                      type="button"
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      aria-label={t("reportGroup.moveUp")}
                      className="rounded p-1 text-neutral-500 hover:bg-neutral-100 disabled:opacity-30"
                    >
                      <ArrowUp size={14} aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(index, 1)}
                      disabled={index === members.length - 1}
                      aria-label={t("reportGroup.moveDown")}
                      className="rounded p-1 text-neutral-500 hover:bg-neutral-100 disabled:opacity-30"
                    >
                      <ArrowDown size={14} aria-hidden />
                    </button>
                  </li>
                );
              })}
            </ol>
            <p className="mt-2 border-t border-neutral-200 pt-2 text-xs text-neutral-500">
              {t("reportGroup.membersHint")}{" "}
              <Link to={`/projects/${slug}?tab=config`} className="font-semibold text-primary hover:underline">
                {t("reportGroup.membersHintLink")}
              </Link>
            </p>
          </Section>
        </div>

        <div className="min-w-0 flex-1">
          <Section
            title={t("reportGroup.filters", { count: group.config.filters.length })}
            description={t("reportGroup.filtersHint")}
          >
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs" data-report-group-matrix="">
                <thead>
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold text-neutral-500">
                      {t("reportGroup.filter")}
                    </th>
                    {members.map((key) => (
                      <th key={key} className="px-2 py-1.5 text-left font-semibold text-neutral-500">
                        {label(draftByKey.get(key)?.report_config.display_name, key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.config.filters.map((f) => (
                    <tr key={f.key} className="border-t border-neutral-200 align-top">
                      <td className="px-2 py-2">
                        <p className="font-semibold text-neutral-900">{label(f.label, f.key)}</p>
                        <p className="font-mono text-xs text-neutral-500">
                          {f.key} · {f.type}
                        </p>
                      </td>
                      {members.map((rkey) => {
                        const decision = bindings[rkey]?.[f.key];
                        const state = !decision
                          ? "missing"
                          : "not_applicable" in decision
                            ? "not_applicable"
                            : "uses";
                        return (
                          <td key={rkey} className="px-2 py-2" data-cell-state={state}>
                            <select
                              className={
                                "w-full rounded-md border px-1.5 py-1 text-xs " +
                                (state === "missing"
                                  ? "border-amber-300 bg-amber-50 font-semibold text-amber-900"
                                  : state === "not_applicable"
                                    ? "border-dashed border-neutral-300 bg-white text-neutral-500"
                                    : "border-neutral-300 bg-white text-neutral-900")
                              }
                              value={state}
                              aria-label={t("reportGroup.cellAria", { filter: f.key, report: rkey })}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === "uses") setDecision(f.key, rkey, { column: String(f.column ?? f.key) });
                                else if (v === "not_applicable") setDecision(f.key, rkey, { not_applicable: true });
                                else setDecision(f.key, rkey, null);
                              }}
                            >
                              <option value="uses">{t("reportGroup.uses")}</option>
                              <option value="not_applicable">{t("reportGroup.notApplicable")}</option>
                              <option value="missing">{t("reportGroup.missing")}</option>
                            </select>
                            {decision && "column" in decision && (
                              <input
                                className="mt-1 w-full rounded-md border border-neutral-300 px-1.5 py-1 font-mono text-xs"
                                value={decision.column}
                                aria-label={t("reportGroup.columnAria", { filter: f.key, report: rkey })}
                                onChange={(e) => setDecision(f.key, rkey, { column: e.target.value })}
                              />
                            )}
                            {decision && "not_applicable" in decision && (
                              <input
                                className="mt-1 w-full rounded-md border border-dashed border-neutral-300 px-1.5 py-1 text-xs text-neutral-600"
                                value={decision.reason ?? ""}
                                placeholder={t("reportGroup.reasonPlaceholder")}
                                aria-label={t("reportGroup.reasonAria", { filter: f.key, report: rkey })}
                                onChange={(e) =>
                                  setDecision(f.key, rkey, {
                                    not_applicable: true,
                                    ...(e.target.value ? { reason: e.target.value } : {}),
                                  })
                                }
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-neutral-500">
              <span className="inline-flex items-center gap-1">
                <Badge>{t("reportGroup.uses")}</Badge> {t("reportGroup.legendUses")}
              </span>
              <span className="inline-flex items-center gap-1">
                <Minus size={12} aria-hidden /> {t("reportGroup.legendNotApplicable")}
              </span>
              <span className="inline-flex items-center gap-1 font-semibold text-amber-900">
                {t("reportGroup.missing")}: {t("reportGroup.legendMissing")}
              </span>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
